/**
 * SupabaseBookmarkValidator
 *
 * Validator for bookmark operations.
 * Implements 5 validation skills.
 *
 * Skills:
 * - VALIDATE_FLASHCARD_ID: Validate flashcard ID format
 * - VALIDATE_USER_AUTH: Validate user authentication
 * - VALIDATE_BOOKMARKS_ARRAY: Validate array of bookmark IDs
 * - VALIDATE_NETWORK: Check network availability
 * - RUN_ALL_VALIDATIONS: Run all validations
 */

class SupabaseBookmarkValidator {
  /**
   * Execute a validation skill
   * @param {string} skill - The skill to execute
   * @param {...any} args - Arguments for the skill
   * @returns {object} - Validation result
   */
  execute(skill, ...args) {
    switch (skill) {
      case 'VALIDATE_FLASHCARD_ID':
        return this._validateFlashcardId(args[0]);
      case 'VALIDATE_USER_AUTH':
        return this._validateUserAuth(args[0]);
      case 'VALIDATE_BOOKMARKS_ARRAY':
        return this._validateBookmarksArray(args[0]);
      case 'VALIDATE_NETWORK':
        return this._validateNetwork();
      case 'RUN_ALL_VALIDATIONS':
        return this._runAllValidations(args[0]);
      default:
        console.warn(`[SupabaseBookmarkValidator] Unknown skill: ${skill}`);
        return { valid: false, error: `Unknown skill: ${skill}` };
    }
  }

  /**
   * Validate flashcard ID format
   * @private
   */
  _validateFlashcardId(flashcardId) {
    if (!flashcardId) {
      return { valid: false, error: 'Flashcard ID is required' };
    }

    if (typeof flashcardId !== 'string') {
      return { valid: false, error: 'Flashcard ID must be a string' };
    }

    if (flashcardId.length === 0) {
      return { valid: false, error: 'Flashcard ID cannot be empty' };
    }

    if (flashcardId.length > 255) {
      return { valid: false, error: 'Flashcard ID exceeds maximum length' };
    }

    return { valid: true };
  }

  /**
   * Validate user authentication
   * @private
   */
  _validateUserAuth(userId) {
    if (!userId) {
      return { valid: false, userId: null, error: 'User is not authenticated' };
    }

    if (typeof userId !== 'string') {
      return { valid: false, userId: null, error: 'User ID must be a string' };
    }

    return { valid: true, userId };
  }

  /**
   * Validate array of bookmark IDs
   * @private
   */
  _validateBookmarksArray(bookmarkIds) {
    if (!bookmarkIds) {
      return { valid: false, validIds: [], invalidIds: [], error: 'Bookmarks array is required' };
    }

    if (!Array.isArray(bookmarkIds)) {
      return { valid: false, validIds: [], invalidIds: [], error: 'Bookmarks must be an array' };
    }

    const validIds = [];
    const invalidIds = [];

    bookmarkIds.forEach(id => {
      const validation = this._validateFlashcardId(id);
      if (validation.valid) {
        validIds.push(id);
      } else {
        invalidIds.push({ id, error: validation.error });
      }
    });

    return {
      valid: invalidIds.length === 0,
      validIds,
      invalidIds
    };
  }

  /**
   * Check network availability
   * @private
   */
  _validateNetwork() {
    const available = typeof navigator !== 'undefined' ? navigator.onLine : true;
    return { available };
  }

  /**
   * Run all validations
   * @private
   */
  _runAllValidations(inputs) {
    const results = {};
    let allValid = true;

    if (inputs.flashcardId !== undefined) {
      results.flashcardId = this._validateFlashcardId(inputs.flashcardId);
      if (!results.flashcardId.valid) allValid = false;
    }

    if (inputs.userId !== undefined) {
      results.userAuth = this._validateUserAuth(inputs.userId);
      if (!results.userAuth.valid) allValid = false;
    }

    if (inputs.bookmarkIds !== undefined) {
      results.bookmarksArray = this._validateBookmarksArray(inputs.bookmarkIds);
      if (!results.bookmarksArray.valid) allValid = false;
    }

    results.network = this._validateNetwork();
    if (!results.network.available) allValid = false;

    return { allValid, results };
  }
}

// Export singleton instance
const instance = new SupabaseBookmarkValidator();
export default instance;
export { SupabaseBookmarkValidator };
